import "./SearchFilterPanel.scss";

import PropTypes from "prop-types";

import * as _ from "lodash";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { Button, Well } from "react-bootstrap";
import Toggle from "react-toggle";
import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { FacetFilterComponent } from "./FacetFilterComponent";
import { FacetFilterComponentCollapsible } from "./FacetFilterComponentCollapsible";
import { FacetRangeSelect } from "./FacetRangeSelect";
import { BoolFacet } from "./BoolFacet";
import { FacetCheckbox } from "./FacetCheckbox";
import { CategoryTreeRoot, CategoryBreadcrumbs } from "./CategoryNavigation";
import CollapseFilterWrapper from "./CollapseFilterWrapper";
import { SearchInputFilterPanel } from "./SearchInputFilterPanel";
import CustomerSettingsStore from "../../../../stores/settings/CustomerSettingsStore";
import SearchStore from "../../../../stores/SearchStore";
import * as FacetUtil from "../../../../utils/FacetUtil";

import {
    trackChooseFacet,
    trackChangeRangeFacet,
    trackDeselectFacet,
    trackSearchFilterPanel,
    trackDeselectAllFacets,
    trackToggleSearchField,
    trackToggleCategoryTree
} from "../../../../utils/SearchTrackingUtil";

import { FACET_TYPE_MULTI, FACET_TYPE_NUMBER, FACET_TYPE_BOOL } from "shared/src/types/articlesearch/Facets";
import { SearchQuery } from "shared/src/types/articlesearch/SearchResult";
import { CategoryRoot } from "shared/src/types/articlesearch/Category";
import { FacetResult } from "shared/src/types/articlesearch/Facets";
import { articleSourcesFeatureActive } from "../../../../searchdiscover/toggles";

type Props = {
    query: SearchQuery;
    categories: CategoryRoot | undefined | null;
    excludedFacets: Set<string>;
    facets: Array<FacetResult> | undefined | null;
    queryLinkComponent: any;
    onChangeQuery: (x0: SearchQuery) => void;
    doNotShowAllCategoriesLink?: boolean;
};

const OTHER_NON_BOOL_FACETS = Symbol();
const OTHER_BOOL_FACETS = Symbol();

type FacetGroupConfig = {
    facetGroupName: null | string;
    facetGroupKey: string;
    // If this returns true, the group will be rendered as a simple well,
    // instead of a collapsible section with a title. Currently used for the
    // toggles at the top
    hideTitle?: () => boolean;
    facets?: Array<{
        key: string;
        binaryToggle?: boolean; // for a boolean facet, show a simple checkbox instead of the ternary toggle,
        translateValues?: boolean; // translate the values of the facet.
        visibleIf?: () => boolean; // additional condition for showing the facet
        valuesVisibleIf?: (key: string) => boolean; // additional condition for showing specific facet values
        keysToSortToTop?: string[]; // Keys that always get sorted to the top
        showWithoutData?: () => boolean; // show even if we don't have facet data from the search
    }>;
};

// This configures the layout of the facets in the filter panel. We have various hardcoded groups, and then catch-all
// groups for all remaining facets. The hardcoded groups also have some additional special logic.
const FACET_GROUPS: Array<FacetGroupConfig | Symbol> = [
    {
        facetGroupName: null,
        facetGroupKey: "DELIVERY_MODE",
        facets: [
            {
                key: "delivery_mode",
                translateValues: true,
                visibleIf: () => !CustomerSettingsStore.isFsdFilterAlwaysActive(),
                keysToSortToTop: ["METRO_DELIVERY"]
            }
        ]
    },
    {
        facetGroupName: null,
        facetGroupKey: "CATEGORIES"
    },
    {
        facetGroupName: null,
        facetGroupKey: "PROMOTIONS",
        facets: [
            {
                // hidden on promotion page
                key: "promotion",
                binaryToggle: true
            },
            {
                // hidden on promotion page
                key: "storeHasPromotion",
                translateValues: true
            }
        ]
    },
    {
        facetGroupName: null,
        facetGroupKey: "ASSORTMENT",
        facets: [
            {
                key: "favorite",
                showWithoutData: () => true,
                visibleIf: () => SessionStore.loggedIn()
            },
            {
                key: "in_my_store",
                binaryToggle: true,
                visibleIf: () => false
            },
            {
                key: "web_exclusive",
                binaryToggle: true,
                visibleIf: () => CountryUtil.isMarioCountry()
            },
            {
                key: "articleSources",
                translateValues: true,
                visibleIf: () => CountryUtil.isMarioCountry() && articleSourcesFeatureActive()
            },
            {
                key: "own_brand",
                visibleIf: () => false
            }
        ]
    },
    OTHER_NON_BOOL_FACETS,
    OTHER_BOOL_FACETS
];

type PreparedFacet = {
    binaryToggle?: boolean;
    selected?: boolean | null;
    selectedValues?: {
        [x: string]: boolean;
    };
    selectedRange?: {
        selectedMin: number | undefined | null;
        selectedMax: number | undefined | null;
    };
} & FacetResult;

type PreparedFacetGroup = {
    facetGroupName: string | undefined | null;
    facetGroupKey: string;
    hideTitle: boolean;
    facets: Array<PreparedFacet>;
};

type PreparedFacetGroups = Array<PreparedFacetGroup>;

// This function takes the facets and query, and builds facet groups according to the configuration above.
// eslint-disable-next-line max-statements
function prepareFacetGroups(
    facets: Array<FacetResult>,
    excluded: Set<string>,
    query: SearchQuery,
    intl: { messages: { [k: string]: string } }
): PreparedFacetGroups {
    const result = [];
    const facetMap: Map<string, FacetResult> = new Map();

    const multiFacetMap = FacetUtil.getFacetMap(query.facets);
    const numericFacetMap = FacetUtil.getNumericFacetMap(query.ranges);
    const boolFacetMap = FacetUtil.getBoolFacetMap(query.options);

    for (const facet of facets) {
        facetMap.set(facet.key, facet);
    }
    const groupedFacets: Set<string> = new Set();
    const augmentFacet: (x0: Readonly<PreparedFacet>) => PreparedFacet = (facet) => {
        if (!("dataType" in facet)) {
            return { ...facet };
        } else if (facet.dataType === FACET_TYPE_MULTI || facet.dataType === "additional") {
            let selectedValues = multiFacetMap[facet.key];
            if (facet.key === "delivery_mode" && CustomerSettingsStore.isFsdFilterActive()) {
                // FIXME(FD) super hacky (#MSD-1232)
                selectedValues = { ...selectedValues, METRO_DELIVERY: true };
            }
            return {
                ...facet,
                selectedValues
            };
        } else if (facet.dataType === FACET_TYPE_BOOL) {
            return {
                ...facet,
                selected: boolFacetMap[facet.key]
            };
        } else if (facet.dataType === FACET_TYPE_NUMBER) {
            return {
                ...facet,
                selectedRange: numericFacetMap[facet.key]
            };
        } else {
            return { ...facet };
        }
    };
    for (const group of FACET_GROUPS) {
        if (group === OTHER_NON_BOOL_FACETS) {
            // all non-bool facets that haven't been grouped yet; each in its own group
            for (const facet of facets) {
                if (
                    facet.dataType !== FACET_TYPE_BOOL &&
                    !groupedFacets.has(facet.key) &&
                    !facetIsExcluded(facet.key, facet, excluded)
                ) {
                    groupedFacets.add(facet.key);
                    const facetGroup = {
                        facetGroupName: facet.name,
                        facetGroupKey: facet.key,
                        hideTitle: false,
                        facets: [augmentFacet(facet)]
                    };
                    result.push(facetGroup);
                }
            }
        } else if (group === OTHER_BOOL_FACETS) {
            // all bool facets that haven't been grouped yet; all in one group ("Options")
            const facetGroup = {
                facetGroupName: "",
                facetGroupKey: "booleanFacet",
                hideTitle: false,
                facets: [] as PreparedFacet[]
            };
            for (const facet of facets) {
                if (
                    facet.dataType === FACET_TYPE_BOOL &&
                    !groupedFacets.has(facet.key) &&
                    !facetIsExcluded(facet.key, facet, excluded)
                ) {
                    groupedFacets.add(facet.key);
                    facetGroup.facets.push(augmentFacet(facet));
                }
            }
            if (facetGroup.facets.length > 0) {
                result.push(facetGroup);
            }
        } else if (!(group instanceof Symbol)) {
            const facetGroup = {
                facetGroupName: group.facetGroupName,
                facetGroupKey: group.facetGroupKey,
                hideTitle: !!group.hideTitle && group.hideTitle(),
                facets: [] as PreparedFacet[]
            };
            for (const facet of group.facets ?? []) {
                groupedFacets.add(facet.key);
                let facetData = facetMap.get(facet.key);
                if (facetData && facet.valuesVisibleIf) {
                    facetData = filterValues(facetData, facet.valuesVisibleIf);
                }
                if (facetData && facet.keysToSortToTop) {
                    facetData = sortValuesToTop(facetData, facet.keysToSortToTop);
                }
                if (facetData && facet.translateValues) {
                    facetData = translateValues(facetData, intl);
                }
                if (
                    facetIsExcluded(
                        facet.key,
                        facetData,
                        excluded,
                        facet.showWithoutData ? facet.showWithoutData() : false
                    )
                ) {
                    continue;
                }
                if (facet.visibleIf && !facet.visibleIf()) {
                    continue;
                }
                facetGroup.facets.push(
                    augmentFacet({
                        key: facet.key,
                        ...(facetData ?? {}),
                        binaryToggle: facet.binaryToggle || false
                    })
                );
            }
            if (facetGroup.facets.length > 0 || facetGroup.facetGroupKey === "CATEGORIES") {
                result.push(facetGroup);
            }
        }
    }
    return result;
}

function facetIsExcluded(
    key: string,
    facet: FacetResult | null | undefined,
    excluded: Set<String>,
    allowWithoutData?: boolean
) {
    if (excluded.has(key)) {
        return true;
    } else if (allowWithoutData) {
        return false;
    } else if (!facet) {
        return true;
    } else if (
        facet.dataType === FACET_TYPE_NUMBER &&
        ((_.isNil(facet.numberMax) && _.isNil(facet.numberMin)) || FacetUtil.numericFacetIsTrivial(facet))
    ) {
        return true;
    } else if (
        (facet.dataType === FACET_TYPE_MULTI ||
            facet.dataType === FACET_TYPE_BOOL ||
            facet.dataType === "additional") &&
        (!facet.values || facet.values.length === 0)
    ) {
        return true;
    } else if (facet.dataType === FACET_TYPE_BOOL && FacetUtil.trueAmount(facet) === 0) {
        return true;
    } else {
        return false;
    }
}

function filterValues(facet: FacetResult, predicate: (key: string) => boolean): FacetResult {
    if (!facet.values) {
        return facet;
    }
    const values = facet.values.filter((v) => predicate(v.name));
    return { ...facet, values };
}

function sortValuesToTop(facet: FacetResult, keys: string[]): FacetResult {
    if (!facet.values) {
        return facet;
    }
    const topValues = facet.values.filter((v) => _.includes(keys, v.name));
    const rest = facet.values.filter((v) => !_.includes(keys, v.name));
    const values = topValues.concat(rest);
    return { ...facet, values };
}

function translateValues(facet: FacetResult, intl: { messages: { [k: string]: string } }): FacetResult {
    if (!facet.values) {
        return facet;
    }
    const values = _.compact(
        facet.values.map((value) => {
            const intlKey = "EX_FACET_NAME_" + value.name;
            if (!intl.messages || !intl.messages[intlKey]) {
                return null;
            }
            const displayName = intl.messages[intlKey];
            return { ...value, displayName };
        })
    );
    return { ...facet, values };
}

class SearchFilterPanel extends React.Component<Props> {
    static contextTypes = {
        intl: PropTypes.object.isRequired
    };

    someFacetIsSelected() {
        const multiValueFacets = this.props.query.facets && this.props.query.facets.length > 0;
        const ranges = this.props.query.ranges && this.props.query.ranges.length > 0;
        const options = this.props.query.options && this.props.query.options.length > 0;
        return this.props.query && (multiValueFacets || ranges || options);
    }

    handleNewQuery = (value: string) => {
        const newQuery = { ...this.props.query, q: value };
        trackSearchFilterPanel(value);
        this.props.onChangeQuery(newQuery);
    };

    handleDeselectAllFacets = (event: React.MouseEvent<Button>) => {
        event.preventDefault();
        trackDeselectAllFacets();
        const newQuery = {
            ...this.props.query,
            facets: [],
            ranges: [],
            options: []
        };
        this.props.onChangeQuery(newQuery);
    };

    handleFacetSelect = (key: string, name: string, newState: boolean) => {
        trackChooseFacet(key, name, newState);
        if (key === "delivery_mode" && name === "METRO_DELIVERY") {
            // TODO(FD) this is super weird, but we want the fsd filter to be
            // saved (but not the other ones), so we handle it completely
            // differently
            CustomerSettingsStore.setFsdFilter(newState);
            return;
        }

        const facets = FacetUtil.selectMultiFacet(this.props.query.facets, key, name, newState);
        let newQuery = { ...this.props.query, facets };
        //if a sub promotion is selected, the "all promotions" facet value has to be deselected
        if (key === "storeHasPromotion" && newState) {
            const options = FacetUtil.changeBoolFacet(newQuery.options, "promotion", null);
            newQuery = { ...newQuery, options };
        }
        this.props.onChangeQuery(newQuery);
    };

    handleRangeFacetChange = (
        key: string,
        minValue: number | undefined | null,
        maxValue: number | undefined | null
    ) => {
        trackChangeRangeFacet(key, minValue, maxValue);
        const ranges = FacetUtil.changeNumericFacet(this.props.query.ranges, key, minValue, maxValue);
        const newQuery = { ...this.props.query, ranges };
        this.props.onChangeQuery(newQuery);
    };

    handleBoolFacetChange = (key: string, value: boolean | null) => {
        if (value !== null) {
            trackChooseFacet(key, key, value);
        } else {
            trackDeselectFacet(key);
        }
        const options = FacetUtil.changeBoolFacet(this.props.query.options, key, value);
        let newQuery = { ...this.props.query, options };
        // if the promotion filter is selected, the promotion type filters need to be deselected
        // there's also the reverse case for storeHasPromotion
        // TODO(FD) this could be done more nicely
        if (key === "promotion" && value) {
            const facets = FacetUtil.deselectAttribute(newQuery.facets, "storeHasPromotion");
            newQuery = { ...newQuery, facets };
        }
        this.props.onChangeQuery(newQuery);
    };

    handleDeselectAllValuesOfAttribute = (event: React.SyntheticEvent<HTMLElement>, key: string) => {
        event.preventDefault();
        trackDeselectFacet(key);
        const facets = FacetUtil.deselectAttribute(this.props.query.facets, key);
        const newQuery = _.assign({}, this.props.query, { facets });
        this.props.onChangeQuery(newQuery);
    };

    handleFavoriteFacetChange = (event: any) => {
        const newState = event.target.checked;
        this.handleBoolFacetChange("favorite", newState || null);
    };

    renderFavoriteFacetFilter(isLastToggle: boolean | undefined | null) {
        const options = this.props.query.options || [];
        const favoriteOption = options.find((e) => e.key === "favorite");
        return (
            <div key={"favoriteFacet"} className="ex-facet-toggle">
                <label className={"react-toggle-label" + (isLastToggle ? " no-bottom-margin" : "")}>
                    <Toggle
                        className="test-favorite-toggle"
                        checked={favoriteOption ? favoriteOption.value : false}
                        onChange={this.handleFavoriteFacetChange}
                    />
                    <FormattedMessage id="SHOW_FAVORITES_ASSORTMENT" />
                </label>
            </div>
        );
    }

    renderDeselectAllFacetsButton() {
        if (!this.someFacetIsSelected()) {
            return null;
        }
        return (
            <Well className="ex-deselect-all-facets">
                <Button
                    block
                    bsStyle="link"
                    className="ex-deselect-all-facets-link"
                    onClick={this.handleDeselectAllFacets as any}>
                    <i className="icon-close" />
                    <FormattedMessage id="EX_FILTER_PANEL_FACET_DESELECT_ALL" />
                </Button>
            </Well>
        );
    }

    renderSearchField() {
        // only show the search field on category pages, storelist pages and promotions page
        // TODO(FD) make this configurable from top instead
        if (
            this.props.query.searchPurpose !== SearchStore.categoryPurpose &&
            this.props.query.searchPurpose !== SearchStore.offersPurpose &&
            this.props.query.searchPurpose !== SearchStore.promotionsPurpose
        ) {
            return null;
        }

        return (
            <CollapseFilterWrapper
                caption={<FormattedMessage id="EX_FILTER_PANEL_SEARCH" />}
                initialOpen
                trackToggle={trackToggleSearchField}>
                <SearchInputFilterPanel q={this.props.query.q} onChange={this.handleNewQuery} />
            </CollapseFilterWrapper>
        );
    }

    renderCategoryPanel() {
        if (!this.props.categories || !_.some(this.props.categories.children)) {
            return null;
        }
        return (
            <CollapseFilterWrapper
                caption={<FormattedMessage id="EX_FILTER_PANEL_CATEGORY_CAPTION" />}
                initialOpen
                trackToggle={trackToggleCategoryTree}>
                <div className="ex-category-tree ex-shown-sub-filter-panel">
                    <CategoryBreadcrumbs
                        categoryPath={this.props.query.categoryPath}
                        categories={this.props.categories}
                        doNotShowAllCategoriesLink={this.props.doNotShowAllCategoriesLink}
                        query={this.props.query}
                        queryLinkComponent={this.props.queryLinkComponent}
                    />
                    <CategoryTreeRoot
                        categories={this.props.categories}
                        query={this.props.query}
                        queryLinkComponent={this.props.queryLinkComponent}
                    />
                </div>
            </CollapseFilterWrapper>
        );
    }

    handleRoleToggle = (event: any) => {
        const newState = event.target.checked;
        const newQuery = { ...this.props.query, role: newState ? "customer" : undefined };
        this.props.onChangeQuery(newQuery);
        if (newState) {
            trackChooseFacet("role", "customer", true);
        } else {
            trackDeselectFacet("role");
        }
    };

    renderRoleToggle() {
        if (!SessionStore.isEmployee()) {
            return null;
        }
        const isToggled = this.props.query.role === "customer";
        return (
            <Well>
                <div key={"employeeRoleToggle"} className="ex-facet-toggle">
                    <label className={"react-toggle-label bottom-margin"}>
                        <Toggle className="test-role-toggle" checked={isToggled} onChange={this.handleRoleToggle} />
                        <FormattedMessage id="SHOW_CUSTOMER_ASSORTMENT_ONLY" />
                    </label>
                </div>
            </Well>
        );
    }

    handleOwnBrandFacetChange = (event: any) => {
        const newState = event.target.checked;
        this.handleBoolFacetChange("own_brand", newState || null);
    };

    renderFacet(facet: PreparedFacet) {
        if (facet.key === "favorite") {
            return this.renderFavoriteFacetFilter(false);
        } else if (facet.dataType === FACET_TYPE_BOOL && facet.binaryToggle) {
            const intlKey = "EX_FACET_NAME_" + facet.key.toUpperCase();
            const displayName = this.context.intl ? this.context.intl.formatMessage({ id: intlKey }) : facet.name || "";
            return (
                <FacetCheckbox
                    className={"test-facet-checkbox-" + facet.key}
                    key={facet.key}
                    name={displayName}
                    amount={FacetUtil.trueAmount(facet)}
                    selected={!!facet.selected}
                    onChange={(newState) => this.handleBoolFacetChange(facet.key, newState || null)}
                />
            );
        } else if (facet.dataType === FACET_TYPE_BOOL) {
            return (
                <BoolFacet
                    className={"test-facet-ternary-" + facet.key}
                    key={facet.key}
                    facet={facet}
                    onChange={this.handleBoolFacetChange}
                    value={facet.selected === undefined ? null : facet.selected}
                />
            );
        } else if (facet.dataType === FACET_TYPE_NUMBER) {
            return (
                <FacetRangeSelect
                    className={"test-facet-range-" + facet.key}
                    key={facet.key}
                    facet={facet}
                    selectedValues={facet.selectedRange || {}}
                    onFacetValueChange={this.handleRangeFacetChange}
                />
            );
        } else if (facet.dataType === FACET_TYPE_MULTI || facet.dataType === "additional") {
            const options = this.props.query.options || [];
            const ownBrandOption = options.find((e) => e.key === "own_brand");
            const ownBrandToggle =
                facet.key === "brand" ? (
                    <label className="react-toggle-label">
                        <Toggle
                            className="test-own-brand-toggle"
                            checked={ownBrandOption ? ownBrandOption.value : false}
                            onChange={this.handleOwnBrandFacetChange}
                        />
                        <FormattedMessage id="SHOW_OWN_BRAND_ASSORTMENT" />
                    </label>
                ) : null;
            return (
                <React.Fragment key={facet.key}>
                    {ownBrandToggle}
                    <FacetFilterComponent
                        className={"test-facet-multi-" + facet.key}
                        facet={facet}
                        onFacetSelect={this.handleFacetSelect}
                        onDeselectAllValuesOfAttribute={this.handleDeselectAllValuesOfAttribute}
                        selectedFacets={facet.selectedValues || {}}
                    />
                </React.Fragment>
            );
        } else {
            return null;
        }
    }

    renderFacetGroup(group: PreparedFacetGroup) {
        if (group.facetGroupKey === "CATEGORIES") {
            return this.renderCategoryPanel();
        }
        const facets = group?.facets?.filter((facet) => facet.key !== "fsd") || [];
        if (!facets?.length) {
            // Note: As MSD-1232 is done, it was decided to skip rendering this facet as it has been succeeded by
            //   the option to filter for the FSD delivery mode
            return null;
        }
        if (group.hideTitle) {
            return <Well key={"wrapper-" + group.facetGroupKey}>{facets.map((facet) => this.renderFacet(facet))}</Well>;
        }
        return (
            <FacetFilterComponentCollapsible
                key={"wrapper-" + group.facetGroupKey}
                facetName={group.facetGroupName}
                facetKey={group.facetGroupKey}
                initialOpen>
                {facets.map((facet) => this.renderFacet(facet))}
            </FacetFilterComponentCollapsible>
        );
    }

    render() {
        const facetGroups = prepareFacetGroups(
            this.props.facets || [],
            this.props.excludedFacets,
            this.props.query,
            this.context.intl
        );
        return (
            <div className="leftColumn ex-search-filter-panel">
                {this.renderSearchField()}
                {this.renderRoleToggle()}
                {facetGroups.map((group) => this.renderFacetGroup(group))}
                {this.renderDeselectAllFacetsButton()}
            </div>
        );
    }
}

export default SearchFilterPanel;

export const { contextTypes } = SearchFilterPanel;
